/**
 * Settings → Help: list of every replayable tour with completion state and a
 * Replay button (the NN/g-required permanent retrieval point — tour spec §2.3),
 * plus the "show tips automatically" recovery toggle for the global opt-out
 * (opt-out kills push only; these Replay buttons always work).
 *
 * Tours that need an instance page (a completed memo, a case) resolve one from
 * the store caches; with none available the button disables with a reason.
 */

import { useEffect, useMemo } from 'react';
import { CheckCircle, Circle, MinusCircle, Play } from 'lucide-react';
import { useJobsStore } from '../store/jobsStore';
import { useMattersStore } from '../store/mattersStore';
import { getTour, REPLAYABLE_TOURS, type TourId } from './registry';
import { loadTourState, setTourOptOut } from './storage';
import { useTourStore } from './tourStore';

const STATE_META = {
  completed: { label: 'Completed', icon: CheckCircle, cls: 'text-emerald-600' },
  dismissed: { label: 'Skipped', icon: MinusCircle, cls: 'text-amber-600' },
  unseen: { label: 'Not seen yet', icon: Circle, cls: 'text-legal-400' },
} as const;

export function TourReplayList() {
  const requestTour = useTourStore((s) => s.request);
  const storageVersion = useTourStore((s) => s.storageVersion);
  const bumpStorageVersion = useTourStore((s) => s.bumpStorageVersion);
  const { jobs, fetchJobs } = useJobsStore();
  const { matters, fetchMatters } = useMattersStore();

  // Cache-then-revalidate (slim payloads) so replay targets resolve.
  useEffect(() => {
    fetchJobs();
    fetchMatters();
  }, [fetchJobs, fetchMatters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stored = useMemo(() => loadTourState(), [storageVersion]);

  const completedJob = jobs.find((j) => j.status === 'completed');
  const firstCase = matters[0];

  const rows = REPLAYABLE_TOURS.map((id: TourId) => {
    const tour = getTour(id);
    let path: string | null = tour.launchPath;
    let disabledReason: string | null = null;
    if (id === 'memo') {
      path = completedJob ? `/app/research/${completedJob.id}` : null;
      if (!path) disabledReason = 'Complete a research first';
    } else if (id === 'casefile') {
      path = firstCase ? `/app/cases/${firstCase.id}` : null;
      if (!path) disabledReason = 'Open a case file first';
    }
    const state = stored.seen[id] ?? 'unseen';
    return { tour, path, disabledReason, state };
  });

  return (
    <div className="space-y-3">
      {rows.map(({ tour, path, disabledReason, state }) => {
        const meta = STATE_META[state];
        return (
          <div key={tour.id} className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-legal-800">{tour.label}</p>
              <p className={`flex items-center gap-1 text-xs mt-0.5 ${meta.cls}`}>
                <meta.icon className="h-3 w-3" />
                {meta.label}
              </p>
            </div>
            {path ? (
              <button
                type="button"
                data-track={`settings-tour-replay-${tour.id}`}
                onClick={() => requestTour(tour.id, path)}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-legal-300 text-legal-700 rounded-lg hover:bg-legal-50 transition-colors text-sm font-medium shrink-0"
              >
                <Play className="h-3.5 w-3.5" />
                Replay
              </button>
            ) : (
              <span className="text-xs text-legal-400 shrink-0">{disabledReason}</span>
            )}
          </div>
        );
      })}

      <label className="flex items-center gap-2 pt-2 border-t border-legal-100 text-sm text-legal-600 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!stored.optOut}
          data-track="settings-tour-auto-toggle"
          onChange={(e) => {
            setTourOptOut(!e.target.checked);
            bumpStorageVersion();
          }}
          className="h-4 w-4 rounded border-legal-300"
        />
        Show tips automatically when I visit a section for the first time
      </label>
    </div>
  );
}
